import { RESEARCH_AGENT_ID } from "@repo/app-types/entities/researchAgent";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { chatHeaderIconClassName } from "@repo/ui/chat/chatHeader";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import {
  IconClock,
  IconDots,
  IconLink,
  IconPlus,
  IconShare2,
  IconShareOff,
  IconSquarePlus2,
  IconTrash,
} from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useCallback } from "react";
import { MenuTrigger, Separator } from "react-aria-components";
import { useUpdateMastraThreadMutation } from "../../api/entities/mastraThread.js";
import { appPaths } from "../../app/appPaths.js";
import {
  checkIsThreadSharedWithCompany,
  RESEARCH_THREAD_ID_PARAM,
  type ResearchAgentProps,
  type ResearchAgentThreadMetadata,
} from "./researchAgentContext.js";

export interface ResearchAgentChatMenuProps {
  researchAgent: ResearchAgentProps;
  /** The current thread's metadata (freshest available share state). */
  threadMetadata: ResearchAgentThreadMetadata;
  /** The modal header already offers New chat inline, so its menu omits it. */
  showsNewChatItem?: boolean;
  onShowHistory?: () => void;
  onRequestExpand?: () => void;
}

/** The chat options menu: expand, share, delete, new chat, history. */
export const ResearchAgentChatMenu: FC<ResearchAgentChatMenuProps> = ({
  researchAgent,
  threadMetadata,
  showsNewChatItem = true,
  onShowHistory,
  onRequestExpand,
}) => {
  const { threadId, resourceId, threadResourceId } = researchAgent;
  const isOwnThread = threadResourceId === resourceId;
  const isThreadActionAvailable = threadId !== undefined && isOwnThread;
  const isShared = checkIsThreadSharedWithCompany(threadMetadata);

  const [updateThread] = useUpdateMastraThreadMutation();

  const buildShareUrl = useCallback(() => {
    const { teammateAgentId, playbookId } = threadMetadata;
    if (!threadId) {
      return undefined;
    }

    let pathname: string;
    if (teammateAgentId) {
      pathname = appPaths.teammateAgent.build({ teammateAgentId });
    } else if (playbookId) {
      pathname = appPaths.playbook.build({ playbookId });
    } else {
      return undefined;
    }

    const url = new URL(pathname, window.location.origin);
    url.searchParams.set(RESEARCH_THREAD_ID_PARAM, threadId);
    return url.toString();
  }, [threadMetadata, threadId]);

  const handleShareChat = useCallback(async () => {
    const shareUrl = buildShareUrl();
    if (!threadId || !shareUrl) {
      return;
    }

    // Copy first, while the user gesture's transient activation is still
    // valid — Safari rejects clipboard writes after an awaited network call.
    await navigator.clipboard.writeText(shareUrl);
    await updateThread({
      agentId: RESEARCH_AGENT_ID,
      threadId,
      metadata: {
        shared: [{ with: "company" as const, at: DateTime.now().toISO() }],
      },
    }).unwrap();
  }, [buildShareUrl, threadId, updateThread]);

  const handleCopyShareLink = useCallback(async () => {
    const shareUrl = buildShareUrl();
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
    }
  }, [buildShareUrl]);

  const handleUnshareChat = useCallback(async () => {
    if (!threadId) {
      return;
    }

    await updateThread({
      agentId: RESEARCH_AGENT_ID,
      threadId,
      metadata: { shared: [] },
    }).unwrap();
  }, [threadId, updateThread]);

  const handleArchiveChat = useCallback(async () => {
    if (!threadId) {
      return;
    }

    await updateThread({
      agentId: RESEARCH_AGENT_ID,
      threadId,
      metadata: { archived: true },
    }).unwrap();
    researchAgent.onNewThreadSelect();
  }, [threadId, updateThread, researchAgent]);

  const hasPrimaryMenuGroup =
    onRequestExpand !== undefined || isThreadActionAvailable;
  const showsNewChat = showsNewChatItem && threadId !== undefined;
  const hasSecondaryMenuGroup = onShowHistory !== undefined || showsNewChat;

  if (!hasPrimaryMenuGroup && !hasSecondaryMenuGroup) {
    return null;
  }

  return (
    <MenuTrigger>
      <IconButton
        accessibleLabel="Chat options"
        variant={ButtonVariant.Tertiary}
        icon={<IconDots className={chatHeaderIconClassName} />}
      />
      <SimpleMenuPopover placement="bottom end">
        <SimpleMenu aria-label="Chat options">
          {onRequestExpand ? (
            <ResearchAgentMenuItem
              icon={IconSquarePlus2}
              label="Expand chat"
              onAction={onRequestExpand}
            />
          ) : null}
          {isThreadActionAvailable ? (
            <>
              {isShared ? (
                <>
                  <ResearchAgentMenuItem
                    icon={IconLink}
                    label="Copy share link"
                    onAction={() => void handleCopyShareLink()}
                  />
                  <ResearchAgentMenuItem
                    icon={IconShareOff}
                    label="Unshare"
                    onAction={() => void handleUnshareChat()}
                  />
                </>
              ) : (
                <ResearchAgentMenuItem
                  icon={IconShare2}
                  label="Share & copy link"
                  onAction={() => void handleShareChat()}
                />
              )}
              <ResearchAgentMenuItem
                icon={IconTrash}
                label="Delete"
                onAction={() => void handleArchiveChat()}
              />
            </>
          ) : null}
          {hasPrimaryMenuGroup && hasSecondaryMenuGroup ? (
            <Separator className="my-1 h-px bg-border-light" />
          ) : null}
          {showsNewChat ? (
            <ResearchAgentMenuItem
              icon={IconPlus}
              label="New chat"
              onAction={() => researchAgent.onNewThreadSelect()}
            />
          ) : null}
          {onShowHistory ? (
            <ResearchAgentMenuItem
              icon={IconClock}
              label="Chat history"
              onAction={onShowHistory}
            />
          ) : null}
        </SimpleMenu>
      </SimpleMenuPopover>
    </MenuTrigger>
  );
};

interface ResearchAgentMenuItemProps {
  icon: FC<{ className?: string }>;
  label: string;
  onAction: () => void;
}

const ResearchAgentMenuItem: FC<ResearchAgentMenuItemProps> = ({
  icon: Icon,
  label,
  onAction,
}) => (
  <SimpleMenuItem textValue={label} onAction={onAction}>
    <Icon className="size-4 text-muted-foreground" />
    <span className="min-w-0 flex-1 truncate">{label}</span>
  </SimpleMenuItem>
);
